import { useState, type Dispatch, type SetStateAction } from 'react';

import type { FolderTrailCrumb } from './sharepoint-trail.js';

/**
 * The names of the levels a session walked through, so the breadcrumbs can read as words instead of Graph
 * ids. It is not in the URL and never will be: a display name goes stale the moment somebody renames a
 * folder, and a shareable link that carries one lies to whoever opens it later.
 */
export interface SharePointRememberedPath {
  site: { id: string; name: string } | null;
  drive: { id: string; name: string } | null;
  folders: FolderTrailCrumb[];
}

/** Nothing walked through yet: every level falls back to its generic label. */
export const NO_REMEMBERED_PATH: SharePointRememberedPath = {
  site: null,
  drive: null,
  folders: [],
};

/** The remembered path and the way to add to it, in the shape `useState` hands back. */
export type SharePointRememberedPathState = [
  SharePointRememberedPath,
  Dispatch<SetStateAction<SharePointRememberedPath>>,
];

/**
 * The remembered path held ABOVE the tree, for a surface whose tree comes and goes — the Artifacts tab
 * unmounts it while search hits are on screen, and state that lived inside it would die there, so clearing
 * the box would put the user back on "Site / Library" instead of "Marketing / Documents".
 *
 * The names belong to the connection they were read from, which is what {@link connectionKey} is for:
 * change it and the walk is forgotten. Two tenants hand out the same shaped ids for different documents, so
 * carrying a name across would label one SharePoint's folder with another's. The reset happens during the
 * render that brings the new key in — React's own way of adjusting state to a changed input — so no frame
 * is ever drawn with the previous SharePoint's names.
 *
 * A tree that is always mounted needs none of this and keeps its own state: {@link SharePointBrowser} falls
 * back to an internal `useState` when no caller holds one.
 */
export function useRememberedPath(
  connectionKey: string,
): SharePointRememberedPathState {
  const [remembered, setRemembered] =
    useState<SharePointRememberedPath>(NO_REMEMBERED_PATH);
  const [rememberedFor, setRememberedFor] = useState(connectionKey);
  if (rememberedFor !== connectionKey) {
    setRememberedFor(connectionKey);
    setRemembered(NO_REMEMBERED_PATH);
    return [NO_REMEMBERED_PATH, setRemembered];
  }
  return [remembered, setRemembered];
}
